/**
 * SAGE 300 Credentials Modal
 * 
 * Configure SAGE 300 connection with customer mode and proxy settings.
 * Pattern: Combines AdobeCommerceCredentialsModal + NetsuiteOAuth2Modal patterns
 */

import React, { useState, useEffect } from 'react';
import { Modal, FormLayout, TextField, InlineStack, Badge, Button, Text, Select, Checkbox, Collapsible } from '@shopify/polaris';
import { configApi } from '../../../services/api/configApi';

export function SageCredentialsModal({ open, onClose, onSave, config = {} }) {
  const [formData, setFormData] = useState({
    baseUrl: '',
    apiVersion: 'v1',
    tenant: '-',
    company: '',
    username: '',
    password: '',
    proxyEnabled: false,
    proxyUrl: '',
    proxyUsername: '',
    proxyPassword: ''
  });
  
  const [validation, setValidation] = useState({ running: false, ok: null, message: '' });
  const [saving, setSaving] = useState(false);
  const [showProxy, setShowProxy] = useState(false);
  
  const canSave = formData.baseUrl && formData.company && 
                  (formData.username || config.usernameEnc) && 
                  (formData.password || config.passwordEnc);
  
  // Load config when modal opens (from DB via config prop)
  useEffect(() => {
    if (open && config && Object.keys(config).length > 0) {
      setFormData(prev => ({
        ...prev,
        baseUrl: config.baseUrl || '',
        apiVersion: config.apiVersion || 'v1',
        tenant: config.tenant || '-',
        company: config.company || '',
        username: config.usernameEnc ? '****' : '',
        password: config.passwordEnc ? '****' : '',
        proxyEnabled: config.proxy?.enabled || false,
        proxyUrl: config.proxy?.url || '',
        proxyUsername: config.proxy?.usernameEnc ? '****' : '',
        proxyPassword: config.proxy?.passwordEnc ? '****' : ''
      }));
      setShowProxy(config.proxy?.enabled || false);
    } else if (open) {
      // Reset to blanks if no config exists
      setFormData({
        baseUrl: '',
        apiVersion: 'v1',
        tenant: '-',
        company: '',
        username: '',
        password: '',
        proxyEnabled: false,
        proxyUrl: '',
        proxyUsername: '',
        proxyPassword: ''
      });
      setShowProxy(false);
    }
    setValidation({ running: false, ok: null, message: '' });
  }, [open, config]);
  
  const handleSave = async () => {
    setSaving(true);
    
    try {
      const payload = {
        baseUrl: formData.baseUrl,
        apiVersion: formData.apiVersion,
        tenant: formData.tenant,
        company: formData.company,
        username: formData.username === '****' ? undefined : formData.username,
        password: formData.password === '****' ? undefined : formData.password,
        proxy: {
          enabled: formData.proxyEnabled,
          url: formData.proxyUrl,
          username: formData.proxyUsername === '****' ? undefined : formData.proxyUsername,
          password: formData.proxyPassword === '****' ? undefined : formData.proxyPassword
        }
      };
      
      await configApi.saveSageConfig(payload);
      
      if (onSave) onSave();
      onClose();
    } catch (error) {
      // Save failed
      setValidation({ running: false, ok: false, message: error.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };
  
  const handleTestConnection = async () => {
    setValidation({ running: true, ok: null, message: 'Testing connection...' });
    
    try {
      const result = await configApi.validateSageConnection();
      
      if (result.success && result.connected) {
        setValidation({ running: false, ok: true, message: 'Connection successful!' });
      } else {
        setValidation({ running: false, ok: false, message: result.error || 'Connection failed' });
      }
    } catch (error) {
      setValidation({ running: false, ok: false, message: error.message });
    }
  };
  
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="SAGE 300 Configuration"
      primaryAction={{
        content: 'Save',
        onAction: handleSave,
        disabled: !canSave,
        loading: saving
      }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <FormLayout>
          <Text variant="headingMd" as="h3">Connection Settings</Text>
          
          <TextField
            label="Base URL"
            value={formData.baseUrl}
            onChange={(value) => setFormData(prev => ({ ...prev, baseUrl: value }))}
            autoComplete="off"
            helpText="Full SAGE 300 Web API URL"
          />
          
          <TextField
            label="Company ID"
            value={formData.company}
            onChange={(value) => setFormData(prev => ({ ...prev, company: value }))}
            placeholder="PICTST"
            autoComplete="off"
          />
          
          <InlineStack gap="400">
            <div style={{ flex: 1 }}>
              <TextField
                label="Username"
                value={formData.username}
                onChange={(value) => setFormData(prev => ({ ...prev, username: value }))}
                autoComplete="off"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label="Password"
                value={formData.password}
                onChange={(value) => setFormData(prev => ({ ...prev, password: value }))}
                type="password"
                autoComplete="off"
              />
            </div>
          </InlineStack>
          
          <Button onClick={() => setShowProxy(!showProxy)}>
            {showProxy ? 'Hide' : 'Show'} Proxy Settings
          </Button>
          
          <Collapsible open={showProxy}>
            <FormLayout>
              <Checkbox
                label="Enable QuotaGuard Proxy"
                checked={formData.proxyEnabled}
                onChange={(checked) => setFormData(prev => ({ ...prev, proxyEnabled: checked }))}
              />
              
              {formData.proxyEnabled && (
                <>
                  <TextField
                    label="Proxy URL"
                    value={formData.proxyUrl}
                    onChange={(value) => setFormData(prev => ({ ...prev, proxyUrl: value }))}
                    placeholder="http://proxy-url"
                  />
                  <InlineStack gap="400">
                    <div style={{ flex: 1 }}>
                      <TextField
                        label="Proxy Username"
                        value={formData.proxyUsername}
                        onChange={(value) => setFormData(prev => ({ ...prev, proxyUsername: value }))}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <TextField
                        label="Proxy Password"
                        value={formData.proxyPassword}
                        onChange={(value) => setFormData(prev => ({ ...prev, proxyPassword: value }))}
                        type="password"
                      />
                    </div>
                  </InlineStack>
                </>
              )}
            </FormLayout>
          </Collapsible>
          
          {validation.message && (
            <InlineStack gap="200">
              <Badge tone={validation.ok ? 'success' : validation.ok === false ? 'critical' : 'info'}>
                {validation.ok ? '✓ Connected' : validation.ok === false ? '✗ Failed' : 'Testing...'}
              </Badge>
              <Text as="span">{validation.message}</Text>
            </InlineStack>
          )}
          
          <Button
            onClick={handleTestConnection}
            disabled={!formData.baseUrl || !formData.company}
            loading={validation.running}
          >
            Test Connection
          </Button>
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
}

